import Vue from "vue"
import Vuex, { ActionTree, MutationTree } from "vuex"

import "@/scripts/array-operations"
import setBrandingLink from "@/scripts/brandingLink"

import { cases } from "./cases"
import { features } from "./features"
import { setup } from "./setup"

import { CasesNotification, NotificationType, Team, Field, TemplateDetails } from "@/types"
import { CurrentLogin, LoginRequest, LoginResultTenant, LoginState } from "@/types/loginRequest"
import { IntegrationSummary } from "@/types/integration"
import { UserSettings, UserSettingsKeys } from "@/types/userSettings"
import { Subscription } from "@/types/subscription"

import api from "@/services/api"
import apiTenant from "@/services/api_Tenant"
import apiNotification from "@/services/api_Notification"
import integrationApi from "@/services/api_Integrations"
import hubNotificationService from "@/services/hubNotificationService"
import apiRecordNameSettings from "@/services/api_RecordNameSettings"
import apiCaseNameSettings from "@/services/api_CaseNameSettings"
import subscriptionService from "@/services/api/subscriptionService"
import { CurrentUser, SystemUser, UserPreferences, UserWithFields } from "@/types/user"
import { RawLocation, Route } from "vue-router"
import apiUserStatus from "@/services/api_UserStatus"

import { UserStatus } from "@/types/userStatus"
import { DefaultValueObject } from "@/types/tenantSetting"
import ConnectionStatus from "@/types/connectionStatus"
import { TenantFeatures } from "@/types/tenantFeatures"
import { isEqual } from "lodash"
import { getStylesheetName, isNewStyle } from "@/utils/ThemeLoader"
import { getCookieValue } from "@/utils/CookieHelper"
import { largeAreaSize } from "@/constants/area"
import DataCache from "@/caching/caching"
import { CachedItemType } from "@/types/caching/cachedItemType"
import apiDefaultCache from "@/services/apiDefaultCache"

Vue.use(Vuex)

export interface StateRouter {
	currentRoute: Route,
	push: (location: RawLocation) => void,
	resolve: (
		to: RawLocation,
		current?: Route,
		append?: boolean
	) => {
		route: Route,
	},
	go: (n: number) => void
}

export interface BaseState {
	router: StateRouter | null,
	screenWidth: number
	me: CurrentUser | null
	userSettings: UserSettings | null
	tenantEnabledFeatures: { [key: string]: boolean }
	userPictureUrls: { [key: string]: string }
	subscription: Subscription | null
	teams: Team[]
	requestingUserPictureUrls: { [key: string]: Promise<string> }
	search: string
	darkMode: boolean
	browserNotificationsPermission: boolean
	notifications: CasesNotification[]
	caseName: string
	recordName: string
	redirectOnLogin: string | null
	integrations: IntegrationSummary[],
	tenantSettings: { [key: string]: any} | null,
	selectTenants: LoginResultTenant[] | null,
	manualLogout: boolean,
	loginState: LoginState,
	currentLogin: CurrentLogin | null,
	systemUser: SystemUser | null,
	userToast: string | null
	availableUserStatuses: UserStatus[]
	materialLoaded: boolean,
	notificationState: ConnectionStatus | null,
	interfaceLoading: boolean,

	/* --- Data Cache --- */
	fieldCache: DataCache<Field> | null,
	templateCache: DataCache<TemplateDetails> | null,
	userCache: DataCache<UserWithFields> | null,
	/* ------------------ */
}

export const state: BaseState = {
	router: null,
	screenWidth: 0,
	me: null,
	userSettings: null,
	tenantEnabledFeatures: {},
	userPictureUrls: {},
	subscription: null,
	teams: [],
	requestingUserPictureUrls: {},
	search: "",
	darkMode: true,
	browserNotificationsPermission: false,
	notifications: [],
	caseName: "Case",
	recordName: "Record",
	redirectOnLogin: null,
	integrations: [],
	tenantSettings: null,
	selectTenants: null,
	manualLogout: false,
	loginState: LoginState.NotLoggedIn,
	currentLogin: null,
	systemUser: null,
	userToast: null,
	availableUserStatuses: [],
	materialLoaded: false,
	notificationState: null,
	interfaceLoading: false,

	/* --- Data Cache --- */
	userCache: new DataCache<UserWithFields>(CachedItemType.User, () => api.usersGet()),
	fieldCache: new DataCache<Field>(CachedItemType.Field, () => api.fields()),
	templateCache: new DataCache<TemplateDetails>(CachedItemType.Template, () => api.templatesWithFields())
	/* ------------------ */
}

export const getters = {
	getCurrentRoute: (state: BaseState) => state.router ? state.router.currentRoute as Route : null,
	getMe: (state: BaseState) => state.me,
	getSubscription: (state: BaseState) => state.subscription,
	hasSubscription: (state: BaseState) => !!(state.subscription && state.subscription.id > 0),
	getUserSettings: (state: BaseState) => state.userSettings,
	getScreenWidth: (state: BaseState) => state.screenWidth,
	getMyId: (state: BaseState) => state.me ? state.me.id : null,
	getUserPictureUrl: (state: BaseState) => (userId: string) => state.userPictureUrls[userId],
	getTeams: (state: BaseState) => state.teams,
	getTeamsById: (state: BaseState) => state.teams.toDictionary("id"),
	getSearch: (state: BaseState) => state.search,
	getBrowserNotificationsPermission: (state: BaseState) => state.browserNotificationsPermission,
	getNotifications: (state: BaseState) => state.notifications,
	getCaseName: (state: BaseState) => state.me && state.me.caseName ? state.me.caseName : state.caseName,
	getRecordName: (state: BaseState) => state.me && state.me.recordName ? state.me.recordName : state.recordName,
	getHasIntegrations: (state: BaseState) => state.integrations.length > 0,
	getTenantSettings: (state: BaseState) => state.tenantSettings,
	getAvailableUserStatuses: (state: BaseState) => state.availableUserStatuses,
	getUseScalingComponent: (state: BaseState) => {
		if (state.tenantSettings && "checkTenantSize" in state.tenantSettings && !!state.tenantSettings["checkTenantSize"]) {
			const checkTenantSize = state.tenantSettings["checkTenantSize"] as DefaultValueObject
			if (checkTenantSize.isEnabled) {
				return (state.me && state.me.tenantSize && state.me.tenantSize > 500)
			}
		}
		return false
	},
	getUseAreaScaling: (state: BaseState) => {
		if (state.me?.tenantEnabledFeatures?.includes(TenantFeatures.AreaScaling)) {
			return (state.me && state.me.tenantSize && state.me.tenantSize > largeAreaSize)
		}
		return false
	},
	getIsSuiteEnabled: (state: BaseState) => {
		return state.me && state.me.tenantEnabledFeatures && state.me.tenantEnabledFeatures.includes(TenantFeatures.Suite)
	},
	getCurrentRouteName: (state: BaseState) => {
		return state.router ? state.router.currentRoute.name : null
	},
	getCurrentRouteMeta: (state: BaseState) => {
		const router = state.router
		return router ? router.currentRoute.meta : null
	},
	getShouldPreventSanitizationOfRichTextFieldCSVDownloads: (state: BaseState) => {
		return state.me && state.me.tenantEnabledFeatures && state.me.tenantEnabledFeatures.includes(TenantFeatures.DisableRichTextInsightsCSVSanitization)
	},
	getUseV2: (state: BaseState, getters: { getCurrentRoute: Route }) => {
		// Reference these first to ensure reactivity gets triggers on route change
		const currentUser = state.me
		const systemUser = state.systemUser

		const route = getters?.getCurrentRoute
		const meta = route?.meta
		if (meta?.v1Only) {
			return false
		}

		if (systemUser) {
			return true
		}

		if (!currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.EnableV2)) {
			return false
		}
		if (currentUser?.preferences?.disableV2) {
			return false
		}

		if (currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.NewUIOptIn)) {
			if (currentUser?.preferences?.enableV2) {
				return true
			} else {
				return false
			}
		}

		return true
	},
	isFeatureEnabled: (state: BaseState) => (featureKey: TenantFeatures) => {
		return state.me?.tenantEnabledFeatures?.includes(featureKey) ?? false
	},
	getDarkMode: (state: BaseState) => {
		const router = state.router
		if (router && router.currentRoute.meta?.forceTheme !== undefined) {
			return router.currentRoute.meta?.forceTheme
		}

		return state.darkMode
	},
	getStylesheet: (state: BaseState, getters: any) => {
		const V2 = getters.getUseV2
		const darkMode = getters.getDarkMode
		return getStylesheetName(darkMode, V2)
	}
}

export const actions: ActionTree<BaseState, BaseState> = {
	async startUp ({ state, commit, dispatch }) {
		try {
			// Browser notifications permission
			const storageItemResult: string | null = localStorage.getItem("browserNotificationsPermission")
			if (storageItemResult && storageItemResult === "true") {
				state.browserNotificationsPermission = true
				if (!(Notification.permission === "granted")) {
					localStorage.setItem("browserNotificationsPermission", "false")
					state.browserNotificationsPermission = false
				}
			} else {
				state.browserNotificationsPermission = false
			}

			// TODO this should be a router guard rather than putting the router in the store
			// See guards.ts, we can use the vuex store inside a router guard

			const currentRoute = state.router?.currentRoute
			if (currentRoute &&
				(!currentRoute.meta || !currentRoute.meta.allowAnon)
			) {
				dispatch("userInit").then(() => {
					if (state.loginState === LoginState.Introduction) {
						if (!state.router) return

						const currentRoute = state.router.currentRoute
						if (currentRoute.name !== "login") {
							// Go to login page to do introduction - ensure that after login, the user comes back here
							commit("setLoginRedirect", currentRoute.path)
							state.router.push({ name: "login" })
						}
					}
				}).catch((error) => {
					console.log(error)
				})
			}
		} catch (err) {
			console.error(err)
			await dispatch("setMe", null)
			commit("setUserSettings", null)

			commit("setNotificationState", {
				connected: false,
				ableToReconnect: false
			})
		}
	},

	async refreshUserInfo ({ commit, dispatch }) {
		try {
			let user = await api.me()
			await dispatch("setMe", user)
		} catch (err) {
			console.error(err)
		}
	},

	async logIn ({ state, commit, dispatch }, loginDetails: LoginRequest) {
		const loginResult = await api.login(loginDetails)
		if (!loginResult) {
			return
		}

		commit("setCurrentLogin", {
			allowSystemAdmin: loginResult.systemAdmin || false,
			requiresMfaSetup: loginResult.requiresMfaSetup || false,
			supportAccess: loginResult.supportAccess,
			requiresIntroduction: loginResult.requiresIntroduction
		})

		if (loginResult.loginTenants) {
			commit("setSelectTenants", loginResult.loginTenants)
			commit("setLoginState", LoginState.PendingTenantSelect)
			return
		} else if (loginResult.requiresMfa) {
			commit("setLoginState", LoginState.PendingMfa)
			return
		}

		dispatch("finishLogin")
	},

	async finishLogin ({ state, dispatch, commit }) {
		dispatch("userInit", {
			loginState: state.currentLogin && state.currentLogin.requiresIntroduction
				? LoginState.Introduction
				: LoginState.LoggedIn,
			redirect: true })
	},

	async finishIntroduction ({ state, dispatch, commit }) {
		commit("setCurrentLogin", {
			...state.currentLogin,
			requiresIntroduction: false
		})
		dispatch("loginRedirect")
	},

	async loginRedirect ({ state, dispatch, commit }) {
		let redirectPath = ""
		let configureLandingPageEnabled = state.tenantEnabledFeatures && state.tenantEnabledFeatures[TenantFeatures.ConfigureLandingPage]
		if (configureLandingPageEnabled) {
			redirectPath = state.teams?.find(team => !!team.landingPagePath)?.landingPagePath || "/cases/New"
		} else {
			redirectPath = "/dashboard"
		}

		if (state.router) {
			state.router.push({ path: (state.redirectOnLogin ? state.redirectOnLogin : redirectPath) })
		}

		commit("setLoginRedirect", null)
	},

	async selectTenant ({ state, commit, dispatch }, { id, userId }: LoginResultTenant) {
		const result = await apiTenant.selectTenant(id, userId)
		commit("setSelectTenants", null)

		await dispatch("clearDataCaches")

		if (state.currentLogin) {
			// Set requires intro on current login if needed
			commit("setCurrentLogin", {
				...state.currentLogin,
				requiresIntroduction: result.requiresIntroduction
			})
		}

		if (result.requiresMfa) {
			commit("setLoginState", LoginState.PendingMfa)

			if (state.router!.currentRoute.name !== "home") {
				state.router!.push({ name: "home" })
			}
		} else {
			await dispatch("userInit", {
				loginState: result.requiresIntroduction ? LoginState.Introduction : LoginState.LoggedIn,
				redirect: false
			})

			if (state.loginState === LoginState.Introduction) {
				if (state.router!.currentRoute.name !== "login") {
					// Send to login page to do introduction
					state.router!.push({ name: "login" })
				}
				return
			}
			commit("setLoginState", LoginState.LoggedIn)
			let redirectPath = ""
			let configureLandingPageEnabled = state.tenantEnabledFeatures && state.tenantEnabledFeatures[TenantFeatures.ConfigureLandingPage]
			if (configureLandingPageEnabled) {
				redirectPath = state.teams?.find(team => !!team.landingPagePath)?.landingPagePath || "/cases/New"
			} else {
				redirectPath = "/dashboard"
			}

			const newPath = state.router!.resolve({ path: (state.redirectOnLogin ? state.redirectOnLogin : redirectPath) })

			if (newPath.route.fullPath === state.router!.currentRoute.fullPath) {
				// You can't go to a route that your already on. However, we need to reload to update.
				state.router!.go(0)
			} else {
				state.router!.push({ path: (state.redirectOnLogin ? state.redirectOnLogin : redirectPath) })
			}
			commit("setLoginRedirect", null)
		}
	},

	async userInit ({ state, dispatch, commit },
		{ loginState, redirect }: {loginState: LoginState, redirect: boolean} = { loginState: LoginState.LoggedIn, redirect: false }) {
		commit("setLoginState", LoginState.Waiting)
		let user = await api.me()
		if (!(user.tenantEnabledFeatures && user.tenantEnabledFeatures.includes(TenantFeatures.RedirectUserAfterInterceptedLogin))) {
			commit("setLoginRedirect", null)
		}

		// Redirecting from OAuth nulls the state.redirectOnLogin value, using existing cookie to set when redirect is enabled.
		let attemptedURL = getCookieValue("attemptedUrl")
		if ((user.tenantEnabledFeatures &&
				user.tenantEnabledFeatures.includes(TenantFeatures.RedirectUserAfterInterceptedLogin) &&
				attemptedURL)) {
			let path = attemptedURL.split("#").pop()
			if (path) {
				state.redirectOnLogin = path
			}
		}

		const introductionEnabled = user.tenantEnabledFeatures && user.tenantEnabledFeatures.includes(TenantFeatures.FirstTimeLogin)
		if (loginState === LoginState.Introduction) {
			if (!introductionEnabled) {
				// If we're not going to do the introduction, skip straight to logged in
				loginState = LoginState.LoggedIn
			}
		} else if (!user.hasDoneFirstLogin && introductionEnabled) {
			// Go to introduction. This state can be hit if the user logs in via SAML
			loginState = LoginState.Introduction
		}

		commit("setLoginState", loginState)

		if (redirect) {
			if (loginState !== LoginState.Introduction) {
				dispatch("loginRedirect")
			}
		}

		if (!user.tenantId) {
			// System user
			await dispatch("connectNotifications")
			commit("setSystemUser", user)

			return
		}

		commit("setSystemUser", null)
		await dispatch("setMe", user)
		await dispatch("loadSubscription")

		try {
			commit("setUserSettings", await api.userSettingsGet(user.id))
			commit("setTeams", await api.teamsGet())
		} catch (err) {
			console.error(err)
		}

		await dispatch("connectNotifications")
		await dispatch("loadTenantSettings")

		commit("setup/setDataSources", await api.dataSources())

		if (!state.me!.recordName) {
			await dispatch("getRecordName")
		}
		if (!state.me!.caseName) {
			await dispatch("getCaseName")
		}

		if (state.me && state.me.isAdmin) {
			integrationApi.integrationsGetActive().then(integrations =>
				commit("setIntegrations", integrations))
		}

		await dispatch("loadAvailableUserStatuses")
	},

	async logOut ({ commit, state, dispatch }, redirectBackOnLogin: boolean = false) {
		hubNotificationService.closeConnection()

		console.log("Logging out")
		await dispatch("setMe", null)
		commit("setLoginState", LoginState.NotLoggedIn)
		commit("setSubscription", null)
		commit("setUserSettings", null)
		commit("setIntegrations", [])
		commit("setTenantSettings", null)
		commit("setSelectTenants", null)
		commit("setCurrentLogin", null)
		commit("setSystemUser", null)
		commit("setAvailableUserStatuses", [])

		dispatch("cases/detailsUnload")
		if (state.router!.currentRoute.path !== "/login") {
			commit("setLoginRedirect", redirectBackOnLogin ? state.router!.currentRoute.path : null)
		}

		(state as any).cases.viewsService.reset()

		if (state.router!.currentRoute.name !== "login") {
			await state.router!.push({ name: "login" })
		}
	},

	async connectNotifications ({ commit, state, dispatch }) {
		if (hubNotificationService) {
			hubNotificationService.closeConnection()
		}

		// Get existing user notification
		commit("setNotifications", await apiNotification.notificationList())

		// Open notifications connection
		hubNotificationService.notLoggedIn = () => dispatch("logOut")
		hubNotificationService.connectionStateChanged = (newConnectionState: boolean) => commit("setNotificationState", {
			connected: newConnectionState,
			ableToReconnect: true
		})

		const connection = await hubNotificationService.openConnection()
		if (connection) {
			connection.on("notifications", (incomingNotifications: CasesNotification[]) => {
				const newNotifications: CasesNotification[] = []
				incomingNotifications.forEach(incomingNotification => {
					if (state.notifications!.some(n => n.id === incomingNotification.id) === false) {
						newNotifications.push(incomingNotification)
					}
				})

				if (newNotifications.length > 0) {
					const existingNotifications: CasesNotification[] = state.notifications
					state.notifications = []
					state.notifications.push(...newNotifications)
					state.notifications.push(...existingNotifications)

					commit("setNotifications", state.notifications)

					// Browser notifications
					if (state.browserNotificationsPermission && Notification.permission === "granted") {
						newNotifications.reverse().forEach(async n => {
							const options: NotificationOptions = {
								body: n.message,
								data: { casesNotification: n },
								icon: "images/icons/sv mark color 48px.png",
								tag: n.id
							}

							let notification = new Notification(n.title, options)
							notification.onclick = async function (_event) {
								// DS
								await apiNotification.notificationDelete(notification.data.casesNotification)

								// Store
								state.notifications.forEach((n, index, array) => {
									if (n.id === notification.data.casesNotification.id) {
										array.splice(index, 1)
									}
								})

								commit("setNotifications", state.notifications)

								// Route
								if (notification.data.casesNotification.type === NotificationType.RecordAssignedToUser ||
									notification.data.casesNotification.type === NotificationType.RecordUpdatedByAnotherUser) {
									const newPath: string = `/cases/${notification.data.casesNotification.data}`
									if (state.router!.currentRoute.path !== newPath) {
										state.router!.push({ path: newPath })
									}
								}
							}
						})
					}
				}
			})
			connection.on("refresh", async (refreshItemType: CachedItemType) => {
				switch (refreshItemType) {
					case CachedItemType.Template:
						commit("cases/addPendingRefresh", CachedItemType.Template)
						setDataCachePendingRefresh(state, CachedItemType.Template)
						await dispatch("setMe", await api.me())
						break
					default:
						setDataCachePendingRefresh(state, refreshItemType)
				}
			})
			connection.on("supportexpiresoon", () => {
				commit("setUserToast", "Support access will expire soon")
			})
			connection.on("userupdated", async () => {
				console.log("User updated - refreshing ... ")
				await dispatch("setMe", await api.me())
			})
			connection.on("logout", () => {
				dispatch("logOut")
			})
		}
	},

	async loadSubscription ({ commit, state }) {
		if (state.me && state.me.billingEnabled) {
			commit("setSubscription", await subscriptionService.getSubscriptionAsync())
		}
	},

	async subscriptionInvalid () {
		state.router!.push({ name: "organization" })
	},

	async loadTeams ({ commit }) {
		commit("setTeams", await api.teamsGet())
	},

	async loadUserPictureUrl ({ state }, userId: string) {
		if (!state.userPictureUrls[userId]) {
			if (state.requestingUserPictureUrls[userId] != null) {
				return state.requestingUserPictureUrls[userId]
			}
			return state.requestingUserPictureUrls[userId] = new Promise(async (resolve, reject) => {
				const userDetails = state.userCache?.getById(userId) ?? await apiDefaultCache.userDetails(userId)
				if (!userDetails) return resolve(state.userPictureUrls[userId] = "images/blank_user.png")

				if (userDetails.pictureUrl) {
					return resolve(state.userPictureUrls[userId] = userDetails.pictureUrl!)
				}

				return resolve(state.userPictureUrls[userId] = api.pictureUrl(userId))
			})
		} else {
			return state.userPictureUrls[userId]
		}
	},

	async requestBrowserNotificationsPermission ({ commit }) {
		Notification.requestPermission().then(function (permission) {
			if (permission === "granted") {
				localStorage.setItem("browserNotificationsPermission", "true")
				commit("setBrowserNotificationsPermission", true)
			} else if (permission === "denied") {
				localStorage.setItem("browserNotificationsPermission", "false")
				commit("setBrowserNotificationsPermission", false)
			}
		})
	},

	async processNotification ({ commit }, notification: CasesNotification) {
		// DS
		await apiNotification.notificationDelete(notification)
		// Store
		const updatedNotifications = state.notifications.filter(n => n.id !== notification.id)
		commit("setNotifications", updatedNotifications)

		// Route
		if (notification.type === NotificationType.RecordAssignedToUser || notification.type === NotificationType.RecordUpdatedByAnotherUser) {
			const newPath: string = `/cases/${notification.data}`
			if (state.router!.currentRoute.path !== newPath) {
				state.router!.push({ path: newPath })
			}
		}
	},

	async clearAllNotifications ({ commit }) {
		await apiNotification.clearAllNotifications()

		commit("setNotifications", [])
	},

	async setTheme ({ commit, dispatch }, darkModeTheme) {
		commit("setDarkMode", darkModeTheme)
	},

	async resetBrandingLink () {
		setBrandingLink(null)
	},

	async getRecordName ({ commit }) {
		let recordNameSettings = await apiRecordNameSettings.recordNameSettingsList()
		commit("setRecordName", recordNameSettings ? recordNameSettings.firstOrDefault(s => s.default).title : "Record")
	},

	async getCaseName ({ commit }) {
		let caseNameSettings = await apiCaseNameSettings.caseNameSettingsList()
		commit("setCaseName", caseNameSettings ? caseNameSettings.firstOrDefault(s => s.default).title : "Case")
	},

	async loadTenantSettings ({ commit }) {
		const tenantSettings = await apiTenant.getTenantSettings()
		commit("setTenantSettings", tenantSettings ? tenantSettings.toDictionaryMapValue(sv => sv.setting.key, sv => sv.value) : {})
	},

	async loadTenantSettingsIfUnloaded ({ commit, state, dispatch }) {
		if (state.tenantSettings && Object.keys(state.tenantSettings).length > 0) {
			return
		}
		await dispatch("loadTenantSettings")
	},

	async loadAvailableUserStatuses ({ commit, state }) {
		const availableStatuses = await apiUserStatus.userStatusList()
		commit("setAvailableUserStatuses", availableStatuses)
	},

	async setCurrentUserStatus ({ commit, state }, newUserStatus: UserStatus) {
		if (!state.me) {
			return
		}
		await api.setUserStatus(state.me.id, newUserStatus.id!)
		commit("setCurrentUserStatus", newUserStatus)
	},

	async clearCurrentUserStatus ({ commit, state }) {
		if (!state.me) {
			return
		}
		await api.clearUserStatus(state.me.id)
		commit("clearCurrentUserStatus")
	},
	async updateUserPreferences ({ commit, state, dispatch }, userPreferences: UserPreferences) {
		if (!userPreferences || isEqual(userPreferences, (state.me) ? state.me.preferences : null)) {
			return
		}
		const successful = (await api.updateUserPreferences(userPreferences))
		if (successful) {
			commit("setUserPreferences", userPreferences)
		}
	},
	/**
	 * Updates the currently logged-in user
	 * Updates their theme, allowing the new interface to be loaded when the user logs in & cleared when they log out
	 * @param me The user to update
	 */
	async setMe ({ commit, dispatch, state }, me: CurrentUser | null) {
		commit("setMe", me)
		let darkMode = state.darkMode
		if (me && me.settings && me.settings.settings && me.settings.settings[UserSettingsKeys.DarkMode] != null) {
			darkMode = !!me.settings.settings[UserSettingsKeys.DarkMode]
		}

		await dispatch("setTheme", darkMode)
	},

	async initDataCaches ({ commit, state }) {
		console.log("Initializing data caches")
		state.userCache = state.userCache ?? new DataCache<UserWithFields>(CachedItemType.User, () => api.usersGet())
		state.fieldCache = state.fieldCache ?? new DataCache<Field>(CachedItemType.Field, () => api.fields())
		state.templateCache = state.templateCache ?? new DataCache<TemplateDetails>(CachedItemType.Template, () => api.templatesWithFields(), undefined, true)
	},

	async clearDataCaches ({ state, dispatch }) {
		console.log("Clearing data caches")
		state.userCache = null
		state.fieldCache = null
		state.templateCache = null
		await dispatch("initDataCaches")
	}
}

export const mutations: MutationTree<BaseState> = {
	setRouter: (state: BaseState, router: StateRouter) => {
		state.router = router
	},
	setMe: (state: BaseState, myDetails: CurrentUser) => {
		state.me = myDetails
		state.tenantEnabledFeatures = myDetails && myDetails.tenantEnabledFeatures
			? myDetails.tenantEnabledFeatures!.toDictionaryMapValue(f => f, f => true)
			: {}
		setBrandingLink(myDetails ? myDetails.tenantId : null)
	},
	setSubscription: (state: BaseState, subscription: Subscription) => state.subscription = subscription,
	setUserSettings: (state: BaseState, userSettings: UserSettings) => {
		state.userSettings = userSettings
	},
	setAvailableUserStatuses: (state: BaseState, userStatuses: UserStatus[]) => {
		state.availableUserStatuses = userStatuses
	},
	setCurrentUserStatus: (state: BaseState, userStatus: UserStatus) => {
		if (state.me) {
			state.me.currentStatus = userStatus
		}
	},
	clearCurrentUserStatus: (state: BaseState) => {
		if (state.me) {
			state.me.currentStatus = undefined
		}
	},
	setScreenWidth: (state: BaseState, width: number) => state.screenWidth = width,
	setTeams: (state: BaseState, teams: Team[]) => state.teams = teams,
	setSearch: (state: BaseState, search: string) => state.search = search,
	setDarkMode: (state: BaseState, darkMode: boolean) => state.darkMode = darkMode,
	setBrowserNotificationsPermission: (state: BaseState, browserNotificationsPermission: boolean) => state.browserNotificationsPermission = browserNotificationsPermission,
	setNotifications: (state: BaseState, notifications: CasesNotification[]) => state.notifications = notifications,
	setCaseName: (state: BaseState, caseName: string) => state.caseName = caseName,
	setRecordName: (state: BaseState, recordName: string) => state.recordName = recordName,
	setLoginRedirect: (state: BaseState, routeName: string | null) => state.redirectOnLogin = routeName,
	setIntegrations: (state: BaseState, integrations: IntegrationSummary[]) => state.integrations = integrations,
	setTenantSettings: (state: BaseState, tenantSettings: { [key: string]: any }) => state.tenantSettings = tenantSettings,
	setSelectTenants: (state: BaseState, selectTenants: LoginResultTenant[] | null) => state.selectTenants = selectTenants,
	setManualLogout: (state: BaseState, manualLogout: boolean) => state.manualLogout = manualLogout,
	setLoginState: (state: BaseState, loginState: LoginState) => state.loginState = loginState,
	setCurrentLogin: (state: BaseState, currentLogin: CurrentLogin | null) => state.currentLogin = currentLogin,
	setSystemUser: (state: BaseState, systemUser: SystemUser) => state.systemUser = systemUser,
	setUserToast: (state: BaseState, message: string | null) => state.userToast = message,
	setMaterialLoaded: (state: BaseState, loaded: boolean) => state.materialLoaded = loaded,
	setNotificationState: (state: BaseState, connectionState: ConnectionStatus | null) => state.notificationState = connectionState,
	setUserPreferences: (state: BaseState, userPreferences: UserPreferences) => {
		if (!state.me) {
			return
		}
		state.me.preferences = userPreferences
	},
	setInterfaceLoading: (state: BaseState, loading: boolean) => state.interfaceLoading = loading
}

// #region Helper Functions

function getDataCacheByItemType (state: BaseState, itemType: CachedItemType): DataCache<any> | null {
	switch (itemType) {
		case CachedItemType.User:
			return state.userCache
		case CachedItemType.Field:
			return state.fieldCache
		case CachedItemType.Template:
			return state.templateCache
		default:
			return null
	}
}

function setDataCachePendingRefresh (state: BaseState, itemType: CachedItemType) {
	const cache = getDataCacheByItemType(state, itemType)
	if (!cache) {
		throw new Error("Data Cache not found: " + itemType)
	}
	console.log("Data Cache is stale and will update when needed:", itemType)
	cache.setPendingRefresh(true)
}

// #endregion

export default new Vuex.Store({
	state,
	getters,
	mutations,
	actions,
	modules: {
		cases,
		features,
		setup
	}
})
